import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container,
  Stack,
  Link,
  Divider
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Timeline from '../components/Timeline';
import { GitHub as GitHubIcon, LinkedIn as LinkedInIcon } from '@mui/icons-material';
import AnimatedHeroBackground from '../components/AnimatedHeroBackground';

const careerTimeline = [
  {
    year: '2011 - 2012',
    title: 'Placement Developer',
    company: '8over8',
    description: 'Started my career as a placement student as part of my university course. Worked on a web application, gaining hands-on experience in software development.'
  },
  {
    year: '2013',
    title: 'Junior Developer',
    company: '8over8',
    description: 'Picked up where I left off in my placement year after completing my degree. Working on a web application and gaining foundational knowledge in software development.'
  },
  {
    year: '2015',
    title: 'Software Engineer',
    company: '8over8',
    description: 'Promoted to Software Engineer. Improved technical skills, collaborated on larger-scale projects, and took on more responsibilities within the engineering team.'
  },
  {
    year: '2018',
    title: 'Senior Software Engineer',
    company: '8over8/AVEVA',
    description: 'Advanced to Senior Software Engineer. Led development of key features, mentored junior developers, and drove technical decisions for project implementations.'
  },
  {
    year: '2019',
    title: 'Team Lead',
    company: 'AVEVA',
    description: 'Promoted to Team Lead. Managed a team of developers, coordinated project timelines, and served as a bridge between technical and business stakeholders.'
  },
  {
    year: '2022 - Present',
    title: 'Principal Engineer',
    company: 'AVEVA',
    description: 'Advanced to Principal Engineer. Architecting enterprise solutions, establishing best practices, and providing technical leadership across multiple teams.'
  }
];

const Home: React.FC = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box 
        sx={{ 
          minHeight: '95vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <AnimatedHeroBackground />
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 800 }}>
            <Typography 
              variant="h1" 
              component="h1" 
              sx={{ 
                fontSize: { xs: '2.5rem', md: '4rem', lg: '5rem' },
                fontWeight: 700,
                mb: 2,
                color: 'primary.main', // Solid color for full opacity
                background: 'none',
                backgroundClip: 'unset',
                textFillColor: 'unset',
                WebkitBackgroundClip: 'unset',
                WebkitTextFillColor: 'unset',
              }}
            >
              KHD
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: { xs: '1.75rem', md: '2.5rem', lg: '3.25rem' },
                fontWeight: 700,
                color: 'text.primary', // Solid color for full opacity
                mb: 3
              }}
            >
              Principal Engineer at AVEVA
            </Typography>
            <Typography 
              variant="body1" 
              color="text.primary" 
              sx={{ 
                maxWidth: '600px',
                mb: 4,
                fontSize: '1.125rem',
                lineHeight: 1.7
              }}
            >
              I'm a software engineer specializing in building exceptional digital experiences. 
              Scroll to learn more about my career journey, skills, and projects.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button 
                variant="contained" 
                color="primary" 
                size="large"
                component={RouterLink}
                to="/projects"
                sx={{
                  px: 4,
                  py: 1.5
                }}
              >
                View My Projects
              </Button>
              <Button 
                variant="outlined" 
                color="primary" 
                size="large"
                component={RouterLink}
                to="/contact"
                sx={{
                  px: 4,
                  py: 1.5
                }}
              >
                Contact Me
              </Button>
            </Stack>
            
            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Link href="https://github.com/KHeavyy" target="_blank" color="inherit">
                <GitHubIcon fontSize="large" />
              </Link>
              <Link href="https://www.linkedin.com/in/keith-dick-7967789a/" target="_blank" color="inherit">
                <LinkedInIcon fontSize="large" />
              </Link>
            </Stack>
          </Box>
        </Container>
      </Box>
      
      {/* Career Timeline Section */}
      <Box sx={{ py: 10, backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            textAlign="center" 
            gutterBottom
            sx={{ mb: 2 }}
          >
            My Career Journey
          </Typography>
          <Typography 
            variant="h6" 
            component="p"
            textAlign="center" 
            color="text.secondary"
            sx={{ 
              maxWidth: 'md',
              mx: 'auto',
              mb: 8
            }}
          >
            From Placement Student to Principal Engineer
          </Typography>
          
          <Timeline data={careerTimeline} />
        </Container>
      </Box>
      
      {/* Skills Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            textAlign="center" 
            gutterBottom
            sx={{ mb: 2 }}
          >
            My Skills
          </Typography>
          <Typography 
            variant="h6"
            component="p"
            textAlign="center" 
            color="text.secondary"
            sx={{ 
              maxWidth: 'md',
              mx: 'auto',
              mb: 8
            }}
          >
            Technologies and tools I work with
          </Typography>
          
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Stack spacing={4}>
              <Box>
                <Typography variant="h5" gutterBottom>
                  Front End
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                  JavaScript (ES6+), TypeScript, React, HTML5, CSS3, Material UI, 
                  Fluent UI, Responsive Design, WebSocket, Unit Testing (React Testing Library, Jest),
                  AngularJS, jQuery, OfficeJS, Webpack, Vite
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="h5" gutterBottom>
                  Back End
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                  Node.js, RESTful APIs, T-SQL, Database Design, .NET (8, Core and Framework), 
                  Authentication & Authorization, Auth0, Microservices, Unit Testing (Moq, xUnit)
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="h5" gutterBottom>
                  DevOps & Tools
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                  Git, GitHub, CI/CD, Powershell, YAML, Azure DevOps, Azure, Unit Testing,
                  Performance Optimization, Agile/Scrum, GitHub Actions, ADO Pipelines, Terraform
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Container>
      </Box>
      
      {/* CTA Section */}
      <Box 
        sx={{ 
          py: 12,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" gutterBottom>
              Interested in working together?
            </Typography>
            <Typography 
              color="text.secondary" 
              sx={{ 
                mb: 4,
                fontSize: '1.1rem',
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              I'm always open to discussing new projects, creative ideas or opportunities to be part of your vision.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                size="large"
                component={RouterLink}
                to="/contact"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem'
                }}
              >
                Get In Touch
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                component={RouterLink}
                to="/about"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem'
                }}
              >
                About Me
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
